// Worksnip branding — central source of truth for the logo / wordmark.
//
// THE LOGO LIVES IN ONE PLACE: `frontend/public/logo/`. Two variants:
//   • worksnip-wordmark.svg          — light surfaces (default)
//   • worksnip-wordmark-on-dark.svg  — dark surfaces (cinema mode, dark CTAs)
//
// To replace the logo project-wide, swap those two SVG files. Every
// consumer (the <Wordmark> component, the MP4 export's canvas painter,
// any future surface) reads its path from THIS module — never hardcodes
// the URL. If you add a new logo variant or change the storage path,
// update the constants below and every consumer follows automatically.
//
// Consumers (grep for `LOGO_PATH_LIGHT`):
//   • frontend/src/components/Wordmark.tsx — the React component used by
//     AppShell header, ViewerToolbar, ProcessFlowSlide, AuthVerify,
//     AuthGoogleCallback, AuthVerifyDone.
//   • frontend/src/services/exportPipelineV2.ts — pre-loads the wordmark
//     SVG and rasterizes it into the MP4 export's Process IQ slide.
//
// Out-of-scope (separate bundles, can't import from frontend/src):
//   • `extension/` — the Chrome MV3 sidepanel + popup ship their own
//     copy under `extension/public/assets/logo/`. Keep that copy in sync
//     manually when the frontend logo changes. (A build-time copy step
//     would remove this trap; out of scope for v0.4.x.)

/** Public URL for the light-theme wordmark (dark ink on light background). */
export const LOGO_PATH_LIGHT = '/logo/worksnip-wordmark.svg';

/** Public URL for the on-dark wordmark (white + accent on dark background). */
export const LOGO_PATH_DARK = '/logo/worksnip-wordmark-on-dark.svg';

/** Public URL for the wordmark WITH the ".com" suffix. Used as the optional
 *  bottom-left brand watermark burned into the MP4 export (platform-admin
 *  "Include worksnip.com logo" option). Ships with the same baked-in fill
 *  fallbacks as the plain wordmark so it rasterizes correctly on canvas. */
export const LOGO_PATH_DOTCOM = '/logo/worksnip-wordmark-dotcom.svg';

/** Width / height ratio of the ".com" wordmark, from its viewBox (5195.99 × 700). */
export const LOGO_DOTCOM_ASPECT_RATIO = 5195.99 / 700;

/**
 * Width / height ratio of the wordmark SVG, derived from its viewBox
 * (3804.75 × 700). The MP4 export's canvas painter uses this to compute
 * width from a chosen height without measuring the SVG element. Update
 * this if the viewBox changes — `<Wordmark>` doesn't need it because it
 * lets the browser auto-scale `width: auto`.
 */
export const LOGO_ASPECT_RATIO = 3804.75 / 700;

// ─────────────────────────────────────────────────────────────────────────
// Logomark — the K monogram used as an avatar / favicon / standalone mark.
//
// Two OFFICIAL variants (rounded-square corners, brand red K). Pick by the
// surface's background:
//   • `light` — white background + red K. Use on dark or mauve surfaces
//     (bot avatars, dark headers).
//   • `dark`  — ink-dark background + red K. Use on light surfaces (page
//     headers on the cream paper theme).
//
// Both files live in `frontend/public/logo/`. To restyle the mark
// project-wide, swap the two SVGs and every consumer of these paths
// (the <Logomark> component, future canvas painters) follows.
// ─────────────────────────────────────────────────────────────────────────

/** Public URL for the square-rounded logomark on a white background. */
export const LOGOMARK_PATH_LIGHT = '/logo/logomark-square-light.svg';

/** Public URL for the square-rounded logomark on a dark background. */
export const LOGOMARK_PATH_DARK = '/logo/logomark-square-dark.svg';

/** Public URL for the rounded-square logomark with the brand-yellow K on a
 *  dark tile. Used for the Ask/chat assistant avatar. */
export const LOGOMARK_PATH_YELLOW = '/logo/logomark-square-yellow.svg';

/** Public URL for the rounded-square logomark with the brand-amber K on a
 *  WHITE tile. The chat assistant avatar uses this so the mark reads on the
 *  light thread background instead of a dark tile. */
export const LOGOMARK_PATH_AMBER_WHITE = '/logo/logomark-square-amber-on-white.svg';

/** Public URL for the reverse circular logomark (coral fill + white K).
 *  Use on white / very light surfaces where the brand needs to pop — the
 *  square-light variant disappears against a white background because its
 *  own fill IS white. */
export const LOGOMARK_PATH_CIRCLE = '/logo/logomark-circle.svg';

/** Public URL for the outlined rounded-square logomark (white tile +
 *  coral border + coral K). Quieter than the solid `circle` reverse
 *  mark — picks up the brand colour from the stroke only, so it sits
 *  cleanly inside a coloured surface (e.g. the AI bubble's pale mauve
 *  background) without overwhelming the surrounding chrome. */
export const LOGOMARK_PATH_OUTLINE = '/logo/worksnip-logomark-outline.svg';
